import { Text } from "components/Text";
import React, { useState } from "react";
import { useWidgetPresetSelection } from "hooks/bookAppointmentOnline/useWidgetPresetSelection.hook";
import { useBookAppointmentOnline } from "hooks/bookAppointmentOnline/useBookAppointmentOnline.hook";
import { CONTEXT_TYPES } from "api/bookAppointmentOnline/get-book-appointment-dashboard-items";
import { AppointmentTypeModel } from "models/AppointmentTypeModel/AppointmentType.model";
import { getAppointmentTypeOptions } from "helpers/get-appointment-type-list-option.helper";
import { Select } from "components/Select";
import { Button, Flex, Spin, Typography } from "antd";
import { useMediaQuery, useTheme } from "@mui/system";
import { CheckboxField } from "components/CheckboxField";
import { OptionTypes } from "views/AppointmentBookOnlineWidgetView/types";
import { Form } from "components/Form/style";
import { Props } from "./types";
import { Divider } from "components/Divider";
import { Input } from "components/Input";
import { motion, AnimatePresence } from "framer-motion";
import { PodoUpLogotype21Color } from "assets/icons";
import DateWithTimePicker from "components/DateWithTimePicker/DateWithTimePicker";
import useGetAppointmentTypeOnlineOptionList
    from "hooks/bookAppointmentOnline/useGetApppointmentTypeList.hook";
import useGetDoctorOnlineOptionList from "hooks/bookAppointmentOnline/useGetDoctorList.hook";
import useGetAvailableTimeSlots from "hooks/bookAppointmentOnline/useGetAppointmentOnlineAvailableSlots.hook";
import useGetAvailableDays from "hooks/bookAppointmentOnline/useGetAvailableDays.hook";
import spacing from "theme/spacing.module.scss";
import dayjs from "dayjs";

const { Paragraph } = Typography;

const wrapperVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.35,
            when: "beforeChildren",
            staggerChildren: 0.08,
        },
    },
    exit: {
        opacity: 0,
        y: -10,
        transition: { duration: 0.35 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 },
};

export default function WidgetForm({ selectOption, redirectUrl, redirectOnClick, agreementConsent, personalDataConsent, marketingConsent }: Props) {
    const [context, setContext] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [date, setDate] = useState<Date>(new Date());
    const [currentMonth, setCurrentMonth] = useState<string>(dayjs().format('YYYY-MM'));
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

    const {
        onSubmit,
        error,
        loading: submitLoading,
        watch,
        control,
        setValue,
        resetMutationError,
        awaitingRedirectClick,
        handleRedirectClick,
    } = useBookAppointmentOnline(
        CONTEXT_TYPES.CONTEXT_ALL,
        null,
        dayjs().format('YYYY-MM-DD HH:mm'),
        undefined,
        { agreementConsent, personalDataConsent, marketingConsent },
        redirectUrl,
        redirectOnClick
    );

    const doctorId = watch('doctorId') ?? null;
    const appointmentTypeId = watch('typeId') ?? null;
    const time = watch('time') ?? null;

    const isEnabled = !!context && !!(context === CONTEXT_TYPES.CONTEXT_DOCTOR ? doctorId : appointmentTypeId);

    const {
        data: availableTimeSlotsData,
        isFetching: availableTimeSlotsIsLoading
    } = useGetAvailableTimeSlots({
        doctorId,
        typeId: appointmentTypeId,
        date: date.toISOString(),
        context,
        contextId: context === CONTEXT_TYPES.CONTEXT_DOCTOR ? doctorId : appointmentTypeId,
        isModalOpen: isEnabled,
    });

    const {
        data: availableDaysData,
    } = useGetAvailableDays({
        doctorId,
        typeId: appointmentTypeId,
        month: currentMonth,
        context,
        contextId: context === CONTEXT_TYPES.CONTEXT_DOCTOR ? doctorId : appointmentTypeId,
        enabled: isEnabled,
    });

    const {
        data: serviceListData,
        isLoading: serviceListIsLoading
    } = useGetAppointmentTypeOnlineOptionList(doctorId);
    const {
        data: specialistListData,
        isLoading: specialistListIsLoading
    } = useGetDoctorOnlineOptionList(appointmentTypeId);

    const serviceOptions = serviceListData?.map(
        (item) =>
            new AppointmentTypeModel({
                typeId: item.getId(),
                typeName: item.getName(),
                typeColor: "#1E90FF",
                typeDurationFrom: item.getDurationFrom(),
                typeDurationTo: item.getDurationTo(),
                typePriceFrom: item.getPriceFrom(),
                typePriceTo: item.getPriceTo(),
                typeDescription: item.getDescription(),
                nextAppointmentInterval: "0",
            })
    );
    const optionsService = getAppointmentTypeOptions({ appointmentTypeList: serviceOptions });

    // Derived live from the currently selected specialist's service list, so switching
    // specialists without touching the service picks up that specialist's own price/duration.
    const selectedAppointmentTypeOption = optionsService?.find(
        (v) => v.value === appointmentTypeId
    ) ?? null;

    useWidgetPresetSelection({
        specialistListData,
        optionsService,
        setValue,
        setContext,
    });

    const handleAppointmentTypeSelectChange = (value: string | number, option) => {
        setValue("time", null);
        resetMutationError();
        if  (!value) { // czyszczenie selekta
            setContext(CONTEXT_TYPES.CONTEXT_DOCTOR);
        } else {
            setContext(CONTEXT_TYPES.CONTEXT_SERVICE);
        }
    };

    const handleSpecialistSelectChange = (value: string) => {
        setValue("time", null);
        resetMutationError();
        if  (!value) { // czyszczenie selekta
            setContext(CONTEXT_TYPES.CONTEXT_SERVICE);
        } else {
            setContext(CONTEXT_TYPES.CONTEXT_DOCTOR);
        }
    }

    const doctorSelect = (
        <Select
            showSearch
            name="doctorId"
            label="Specjalista"
            placeholder="Dowolny specjalista"
            control={control}
            notFoundContent={specialistListIsLoading ? <Spin size="small" /> : "Nie znaleziono"}
            options={specialistListData?.map((specialist) => ({
                value: specialist.getId(),
                label: specialist.getName(),
            }))}
            filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
            }
            value={doctorId}
            allowClear
            onChange={handleSpecialistSelectChange}
        />
    );

    const serviceSelect = (
        <Select
            showSearch
            name="typeId"
            label="Usługa*"
            placeholder="Wybierz usługę"
            control={control}
            notFoundContent={serviceListIsLoading ? <Spin size="small" /> : "Nie znaleziono"}
            options={optionsService}
            filterOption={(input, option) =>
                (option?.children.props.children[0].props.children ?? "").toLowerCase().includes(input.toLowerCase())
            }
            onChange={handleAppointmentTypeSelectChange}
            value={selectedAppointmentTypeOption?.labelString}
            allowClear
        />
    );

    const renderSelectOrder = () =>
        selectOption === OptionTypes.SERVICE ? (
            <>
                {serviceSelect}
                {doctorSelect}
            </>
        ) : (
            <>
                {doctorSelect}
                {serviceSelect}
            </>
        );

    if (awaitingRedirectClick) {
        return (
            <Form>
                <Text textAlign="center" $textVariant="h2" $textColor="text500" style={{ marginBottom: spacing.spacingMD }}>
                    Zarezerwowano wizytę
                </Text>
                <Button
                    onClick={handleRedirectClick}
                    type="primary"
                    size="large"
                    block
                >
                    Kliknij, aby zakończyć rejestrację
                </Button>
            </Form>
        );
    }

    return (
        <Form>
            <Text textAlign="center" $textVariant="h2" $textColor="text500" style={{ marginBottom: spacing.spacingXSSM }}>
                Zarezerwuj wizytę
            </Text>
            {renderSelectOrder()}
            {selectedAppointmentTypeOption?.value && (
                <Flex vertical gap={spacing.spacingSM}>
                    <Flex vertical={isSmall} gap={isSmall ? spacing.spacingXXS : undefined} justify="space-between">
                        <Flex style={{ whiteSpace: "nowrap" }}>
                            <Text $textVariant="pBold" $textColor="text500">
                                Cena:&nbsp;
                            </Text>
                            <Text $textVariant="p" $textColor="text500">
                                {`${selectedAppointmentTypeOption.formattedPrice} zł`}
                            </Text>
                        </Flex>
                        <Flex style={{ whiteSpace: "nowrap" }}>
                            <Text $textVariant="pBold" $textColor="text500">
                                Czas trwania:&nbsp;
                            </Text>
                            <Text $textVariant="p" $textColor="text500">
                                {selectedAppointmentTypeOption.formattedDuration}
                            </Text>
                        </Flex>
                    </Flex>

                    <Paragraph
                        ellipsis={{
                            rows: 3,
                            expandable: 'collapsible',
                            expanded,
                            symbol: expanded ? 'Pokaż mniej' : 'Pokaż więcej',
                            onExpand: (_, info) => setExpanded(info.expanded),
                        }}
                        style={{ zIndex: 1 }}
                    >
                        {selectedAppointmentTypeOption.description}
                    </Paragraph>
                </Flex>
            )}
            <Divider color="primaryPurple100" />
            {(appointmentTypeId || (appointmentTypeId && doctorId)) && (
                <>
                    <DateWithTimePicker
                        date={date}
                        setDate={(d) => { setDate(d); resetMutationError(); }}
                        slots={availableTimeSlotsData}
                        loading={availableTimeSlotsIsLoading}
                        name="time"
                        control={control}
                        setValue={setValue}
                        availableDays={availableDaysData}
                        onMonthChange={setCurrentMonth}
                        onSelect={() => resetMutationError()}
                    />
                    <Divider color="primaryPurple100" />
                </>
            )}

            <AnimatePresence mode="wait">
                {time && (
                    <motion.div
                        key="appointment-form"
                        variants={wrapperVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <motion.div variants={itemVariants}>
                            <Input
                                name="lastName"
                                label="Nazwisko *"
                                placeholder="Wpisz nazwisko"
                                control={control}
                                autocomplete="off"
                            />
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <Input
                                name="firstName"
                                label="Imię *"
                                placeholder="Wpisz imię"
                                control={control}
                                autocomplete="off"
                            />
                        </motion.div>

                        <motion.div variants={itemVariants}>
                            <Input
                                name="phoneNumber"
                                label="Telefon *"
                                placeholder="Wpisz nr. telefonu"
                                control={control}
                                autocomplete="off"
                            />
                        </motion.div>

                        {agreementConsent?.Show && (
                            <motion.div variants={itemVariants}>
                                <CheckboxField
                                    name="agreementConsent"
                                    control={control}
                                    label={
                                        agreementConsent.Link ? (
                                            <>
                                                {agreementConsent.Content}
                                                <a href={agreementConsent.Link} target="_blank"
                                                   rel="noopener noreferrer">
                                                    Pokaż.
                                                </a>
                                            </>
                                        ) : agreementConsent.Content
                                    }
                                />
                            </motion.div>
                        )}

                        {personalDataConsent?.Show && (
                            <motion.div variants={itemVariants}>
                                <CheckboxField
                                    name="personalDataConsent"
                                    control={control}
                                    label={personalDataConsent.Content}
                                />
                            </motion.div>
                        )}

                        {marketingConsent?.Show && (
                            <motion.div variants={itemVariants}>
                                <CheckboxField
                                    name="marketingConsent"
                                    control={control}
                                    label={marketingConsent.Content}
                                />
                            </motion.div>
                        )}

                        <motion.div variants={itemVariants}>
                            <CheckboxField
                                name="addToCalendar"
                                control={control}
                                label="Dodaj do kalendarza"
                            />
                        </motion.div>

                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{opacity: 0, y: -5}}
                                    animate={{opacity: 1, y: 0}}
                                    exit={{opacity: 0}}
                                >
                                    <Text
                                        $textColor="accentRed500"
                                        marginBottom={spacing.spacingXS}
                                    >
                                    {error}
                                    </Text>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <motion.div
                            variants={itemVariants}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Button
                                onClick={onSubmit}
                                type="primary"
                                size="large"
                                block
                                disabled={
                                    submitLoading ||
                                    !(appointmentTypeId || date || time || doctorId)
                                }
                                loading={submitLoading}
                            >
                                Umów
                            </Button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <Flex justify="flex-end">
                <PodoUpLogotype21Color
                    width="100px"
                    height="100px"
                    style={{ zIndex: 2, cursor: 'pointer' }}
                    onClick={() => window.open('https://podoup.pl', '_blank', 'noopener,noreferrer')}
                />
            </Flex>
        </Form>
    );
}
